import { ClipboardPaste, Compass, type LucideIcon } from 'lucide-react'

import { GOALS } from '@/features/studies/creation/components/goals/goalsConfig'
import i18n from '@/i18n'

/**
 * One resolver for "what did the user pick", shared by the mega menu trigger and
 * the active chip so the two can never disagree about a label or a glyph.
 *
 * Lives in its own module rather than in StudyTypeControls because the chip and
 * the controls both need it and importing one from the other would make a cycle.
 *
 * Note these ids are the same eight the python `arc_registry` carries, which is
 * why "Ask anything" maps to `explore` (the `generic` arc): it is the no-template
 * lane, not a separate thing.
 */
export const ASK_ANYTHING_ID = 'explore'
/** Not a template: the user brings the questionnaire. */
export const PASTE_QUESTIONNAIRE_ID = 'paste_questionnaire'

export interface StudyTypeAction {
  id: string
  label: string
  icon: LucideIcon
}

export const STUDY_TYPE_ACTIONS: Array<StudyTypeAction> = [
  { id: ASK_ANYTHING_ID, label: 'Ask anything', icon: Compass },
  { id: PASTE_QUESTIONNAIRE_ID, label: 'Paste questionnaire', icon: ClipboardPaste },
]

export interface ResolvedStudyType {
  label: string
  icon: LucideIcon
  /** Null for the two actions: they carry no method, so they carry no accent. */
  category: string | null
}

export function studyTypeActionLabel(id: string): string {
  if (id === ASK_ANYTHING_ID) {
    return i18n.t('homeLab.askAnything', { ns: 'app' })
  }
  if (id === PASTE_QUESTIONNAIRE_ID) {
    return i18n.t('homeLab.pasteQuestionnaire', { ns: 'app' })
  }
  return id
}

export function resolveStudyType(studyTypeId: string | null): ResolvedStudyType | null {
  if (!studyTypeId) return null

  // Actions win the lookup. `explore` is BOTH an action id and a real goal, and
  // in this menu it is the action, so checking actions first is load-bearing.
  const action = STUDY_TYPE_ACTIONS.find((candidate) => candidate.id === studyTypeId)
  if (action) return { label: studyTypeActionLabel(action.id), icon: action.icon, category: null }

  const goal = GOALS.find((candidate) => candidate.id === studyTypeId)
  if (!goal) return null

  // methodTag, not title: the classic MRX name ("Ad Testing") is what the card
  // showed, so it is what the chip has to echo back.
  return { label: goal.methodTag, icon: goal.icon, category: goal.category }
}
